import { resetClientStore } from "@pathwright/auth/src/lib/utils/auth-token"
import { lazy, Suspense, useCallback, useRef, useState } from "react"
import { Redirect } from "react-router-dom"
import InvalidRoute from "./lib/core/routing/InvalidRoute"

const LazyWebAppRoutes = lazy(
  () =>
    import(
      "@pathwright/auth/src/routes/WebAppRoutesForLegacy" /* webpackChunkName: "WebAppRoutesForLegacy" */
    )
)

// Ensures auth state is synced between PW2 adn PW3 clients.
export const useAuthSync = (): (() => Promise<void>) => {
  // Use the current auth_token to determine if auth state should be synced.
  const authTokenRef = useRef<string | null>(localStorage.getItem("auth_token"))
  // Use a ref for the auth sync promise to determine 1) if auth state
  // is currently being sycned and 2) awaiting the current sync process to finish
  // rather than initiating another.
  const authSyncPromiseRef = useRef<Promise<void> | null>(null)

  // Sync auth state from app/web with client/main
  const authSync = useCallback(async () => {
    if (authTokenRef.current !== localStorage.getItem("auth_token")) {
      authTokenRef.current = localStorage.getItem("auth_token")
      await resetClientStore()

      // @ts-ignore
      const { signOut, checkSession } = window.App.getStore("auth").action
      if (localStorage.getItem("auth_token")) {
        // this will check the session and trigger bootstrapping the school store. Bit messy, but it works.
        await checkSession().promise
      } else {
        await signOut().promise
      }
      authTokenRef.current = localStorage.getItem("auth_token")
    }
  }, [])

  const handleAuthSync = useCallback(() => {
    return (authSyncPromiseRef.current ||= authSync().finally(() => {
      authSyncPromiseRef.current = null
    }))
  }, [])

  return handleAuthSync
}

// A Map of the /client routes that should be handled by /legacy
const clientRedirectRoutes = new Map([[/^\/$/, "/"]])

type ClientRoutesProps = {
  authSync: ReturnType<typeof useAuthSync>
}

// Wrap the /client routes with a 404 handler so that we can
// redirect 404 routes to the /client/main router.
const ClientRoutes = ({ authSync }: ClientRoutesProps) => {
  const [redirectUrl, setRedirectUrl] = useState<string>()
  // const authSync = useAuthSync()

  // Called when /client route changes. We can redirect from any /client
  // route that should be handled by /legacy.
  const handleBeforeLoad = async ({ request }: any) => {
    const { pathname } = new URL(request.url)
    // Get the redirectUrl for current route, if any.
    const [_, redirectUrl] =
      Array.from([...clientRedirectRoutes.entries()]).find(([re, path]) =>
        re.test(pathname) ? path : null
      ) || []

    if (redirectUrl) {
      // Before we redirect, handle syncing auth.
      await authSync()
      // Redirect the route.
      setRedirectUrl(pathname)
      // Just defer loading the route entirely.
      return new Promise(() => {})
    }
    return null
  }

  const handle404 = async ({ request }: any) => {
    const url = new URL(request.url)
    const nextRedirectUrl = url.pathname + url.search + url.hash
    // CLI consent is served by Django, so establish its same-origin session
    // before leaving the SPA. The browser's token stays in the browser.
    if (url.pathname === "/auth/cli/authorize/") {
      const token = localStorage.getItem("auth_token")
      if (token) {
        const response = await fetch("/api/private/session/", {
          credentials: "same-origin",
          headers: { Authorization: `Bearer ${token}` }
        })
        if (!response.ok) throw new Error("Could not synchronize your session.")
      }
      window.location.assign(nextRedirectUrl)
      return new Promise(() => {})
    }

    // Before we redirect, handle syncing auth.
    await authSync()

    if (redirectUrl !== nextRedirectUrl) {
      setRedirectUrl(nextRedirectUrl)
      // This may also be fine...
      // window.App.navigate(nextRedirectUrl)
    } else {
      // Likely not reaching here.
      return <InvalidRoute />
    }

    // Loaders should at least return null.
    return null
  }

  return redirectUrl ? (
    <Redirect to={redirectUrl} />
  ) : (
    <Suspense>
      <LazyWebAppRoutes
        onBeforeLoad={handleBeforeLoad}
        // @ts-ignore
        on404={handle404}
      />
    </Suspense>
  )
}

export default ClientRoutes
